import { useState } from 'react'
import styled from 'styled-components'

import { colors, transitions } from '@/styles'
import { formatPhone, parsePhone } from '@/utils/format-phone'
import useFieldFast from '@/hooks/use-field-fast'
import CountryCodeSelect from '@/components/country-code-select'

import Error from './error'
import MaskedInput from './masked-input'

// This helps so the user can autofill
// without affecting before the input mask can be determined.
// This is since the input mask is generated as the user types.
const extra = '9'.repeat(40)

const Wrapper = styled.div`
  --border-color: ${colors.grey};
  height: 60px;
  display: flex;
  transition: border-color ${transitions.ease};
  border: 1px solid var(--border-color);
  border-radius: 6px;
  ${props =>
    props.disabled &&
    `
    --border-color: ${colors.grey};
  `}

  ${props =>
    props.focused &&
    `
    --border-color: ${colors.secondary};
  `}

  ${props =>
    props.hasError &&
    `
    --border-color: ${colors.red} !important;
  `}
`

const PhoneInputBox = styled(MaskedInput)`
  display: block;
  border-radius: 0px;
  border: none;
  flex: 1;
  background: none;
  padding: 0 15px 0 12px;
`

const PhoneInput = ({
  countryCodeAlpha,
  autoFocus = false,
  placeholder,
  disabled,
  value: initialValue = '',
  name,
  ...rest
}) => {
  const [field, meta, helpers] = useFieldFast(name)
  const { value } = field
  const hasError = !!meta.error && meta.touched
  const focus = () => setFocus(true)
  const blur = () => setFocus(false)
  const parsedPhone = parsePhone(initialValue || value)
  const [focused, setFocus] = useState(autoFocus)
  const [countryCodeState, setCountryCodeState] = useState({
    txt_id: countryCodeAlpha?.toLowerCase() || 'ca',
    calling_code: parsedPhone?.countryCode?.toString() || '1'
  })
  // Since we don't want to display the +1 country code, we decode for the input
  // and re-encode it when we save.  This way we have the +1 when its saved in
  // the form.
  const encode = (v, code) => `+${code ?? countryCodeState.calling_code}${v}`
  const decode = v => v?.substring(countryCodeState?.calling_code?.length + 1)
  const setCountryCode = code => {
    const normal = decode(field.value)
    setCountryCodeState(code)
    helpers.setValue(encode(normal, code?.calling_code))
  }
  const getMask = () => {
    try {
      const value = formatPhone(decode(field.value), countryCodeState.txt_id)
      const existingMask = value.replace(/[0-9]/g, '9')
      if (!existingMask) return
      return existingMask + extra
    } catch (e) {
      return extra
    }
  }
  const getMaskValues = value => (value || '').trim().replace(/[0-9]/g, '')
  const format = (...args) => {
    const [newState, oldState] = args
    const { value } = newState
    // We offset the cursor selection
    // to skip over non-numbers
    const current = newState.selection
    let cursorPosition = current ? current.start : null
    const diff =
      getMaskValues(newState?.value).length -
      getMaskValues(oldState?.value).length
    cursorPosition += diff >= 0 ? diff : 0
    const selection = { start: cursorPosition, end: cursorPosition }
    return {
      value,
      selection
    }
  }
  return (
    <>
      <Wrapper
        {...rest}
        focused={focused}
        hasError={hasError}
        disabled={disabled}
      >
        <CountryCodeSelect
          onChange={setCountryCode}
          value={countryCodeState}
          disabled={disabled}
          onFocus={focus}
          onBlur={blur}
          showFlag
        />
        <PhoneInputBox
          hideError
          id={name}
          name={name}
          disabled={disabled}
          beforeMaskedValueChange={format}
          format={getMask()}
          type="tel"
          onFocus={focus}
          onBlur={blur}
          placeholder={placeholder}
          encode={encode}
          decode={decode}
        />
      </Wrapper>
      <Error name={name} />
    </>
  )
}

export default PhoneInput
